"use client"

// Bundle Type Drill-Down — Sheet that opens when a bundle definition row is
// clicked. Three tabs:
//   1. Top SKUs (default) — best-selling SKU variants in this definition's
//      bundle_groups, lazily fetched on open.
//   2. Standalone — per-family standalone breakdown for the bundle's family
//      components. Each family section lists top SKUs sold standalone in
//      the period (i.e. NOT part of any bundle). Composite bundles surface
//      one section per family; pure-SKU-list bundles render a hint that
//      per-family breakdown isn't applicable.
//   3. Instances — per-bundle-group rows with order_name + processed_at.
//      Rows are expandable: chevron reveals the underlying line_items so the
//      user can see exactly which products made up that bundle occurrence.
//
// Each tab triggers its own fetch (lazy per-tab) — switching tabs hits
// `useBundleTypeBreakdown`'s module cache on revisit, so the server only
// computes the section the user is actually looking at. Standalone is the
// heaviest section (period-wide line_items fetch) so it explicitly stays
// behind a tab click.
//
// Edit-bundle-definition flow (name / starts_at / ends_at / notes)
// lives in the Constructor UI on the bundle-rules page, not here. This
// component is read-only.

import { Fragment, useEffect, useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronRight, Package } from "lucide-react"
import { useBundleTypeBreakdown } from "./use-bundle-type-breakdown"
import { computePageWindow } from "./paginate"
import { InstanceLineItemsBreakdown } from "./instance-line-items-breakdown"
import { OrdersLink } from "@/components/shopify/cross-sell/orders-link"
import type {
  BundleBreakdownSection,
  BundleInstanceRow,
  BundleStrategy,
  BundleTypePerformance,
  StandaloneBreakdown,
  TopSkuRow,
} from "@/lib/shopify/bundles/types"

const fmtCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
const fmtInt = new Intl.NumberFormat("en-US")

// Rows per page for the client-side pagers below. The server returns the full
// set (bounded by its own safety ceiling) and each table paginates locally.
const PAGE_SIZE = 25

// Requested row limit for each section — deliberately above the server's MAX so
// the route returns its full (capped) set in one fetch. The drill-down then
// paginates client-side, reusing the hook's cache across page switches.
const FETCH_ALL_LIMIT = 100_000

// Stable empty reference so the pager hook's reset effect doesn't fire every
// render while a section is still loading (rows == null).
const EMPTY_ROWS: readonly never[] = []

// Display format for ISO timestamptz values in the user's timezone.
// Inline `toLocaleDateString({ timeZone })` matches the project pattern
// (see order-detail-drawer.tsx) — no extra helper module required.
function formatTimestampInTz(iso: string | null, timezone: string): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return iso
  }
}

export type DrillDownTab = "skus" | "standalone" | "instances"

/**
 * Map active tab → server `sections` list. Each tab fetches only its own
 * section so opening Top SKUs never pays the cost of period-wide
 * standalone line_items. The hook's module cache short-circuits revisits
 * within TTL.
 */
const SECTIONS_BY_TAB: Record<DrillDownTab, BundleBreakdownSection[]> = {
  skus: ["top_skus"],
  standalone: ["standalone"],
  instances: ["instances"],
}

interface BundleTypeDrillDownProps {
  integrationId: string
  type: BundleTypePerformance
  /** Period from the parent BundlePerformanceView — drives breakdown queries. */
  periodStart: string
  periodEnd: string
  minimumOrderTotal: number
  /**
   * User's timezone (`useAuth().user.timezone` resolved at the page level,
   * "UTC" fallback). Drives every calendar-date display so that ISO timestamps
   * from server (`order_processed_at`) render as the user sees them, not as UTC.
   */
  timezone: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Which tab to land on when the Sheet opens. Lets cell-level click handlers
   * route Bundle-cells to «instances» and (future) Standalone-cells to their
   * own tab. Default «skus» matches the prior row-click behavior.
   */
  initialTab?: DrillDownTab
}

export function BundleTypeDrillDown({
  integrationId,
  type,
  periodStart,
  periodEnd,
  minimumOrderTotal,
  timezone,
  open,
  onOpenChange,
  initialTab = "skus",
}: BundleTypeDrillDownProps) {
  const [activeTab, setActiveTab] = useState<DrillDownTab>(initialTab)

  // Per-tab fetch — `sections` follows the active tab so each tab pays only
  // for its own data. Switching tabs back hits the hook's module cache
  // (5min TTL) instead of refetching, matching the reviewer's
  // «fetch-once-per-tab with cache до закрытия Sheet» direction.
  const breakdown = useBundleTypeBreakdown({
    integrationId,
    bundleDefinitionId: type.bundle_definition_id,
    periodStart,
    periodEnd,
    minimumOrderTotal,
    // Pull the full set per section; pagination happens client-side below.
    topSkusLimit: FETCH_ALL_LIMIT,
    instancesLimit: FETCH_ALL_LIMIT,
    sections: SECTIONS_BY_TAB[activeTab],
    enabled: open,
  })

  // Reset tab on Sheet close so the next opening respects the caller's
  // initialTab (rather than sticking on whatever the user clicked last).
  useEffect(() => {
    if (!open) setActiveTab(initialTab)
  }, [open, initialTab])

  // Also re-apply initialTab when the prop changes while Sheet is open — lets
  // a click on a different cell of the same row switch tabs without close-open.
  useEffect(() => {
    if (open) setActiveTab(initialTab)
  }, [initialTab, open])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="break-words">{type.display_label}</SheetTitle>
          <SheetDescription className="break-all text-xs font-mono">
            {type.bundle_definition_id}
          </SheetDescription>
        </SheetHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as DrillDownTab)}
          className="mt-4"
        >
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="skus">Top SKUs</TabsTrigger>
            <TabsTrigger value="standalone">Standalone</TabsTrigger>
            <TabsTrigger value="instances">Instances</TabsTrigger>
          </TabsList>

          <TabsContent value="skus" className="mt-4">
            <TopSkusList
              isLoading={breakdown.isLoading}
              error={breakdown.error}
              rows={breakdown.breakdown?.top_skus ?? null}
              total={breakdown.breakdown?.total_skus ?? null}
            />
          </TabsContent>

          <TabsContent value="standalone" className="mt-4">
            <StandaloneBreakdownView
              isLoading={breakdown.isLoading}
              error={breakdown.error}
              breakdown={breakdown.breakdown?.standalone_breakdown ?? null}
            />
          </TabsContent>

          <TabsContent value="instances" className="mt-4">
            <InstancesList
              integrationId={integrationId}
              periodStart={periodStart}
              periodEnd={periodEnd}
              timezone={timezone}
              isLoading={breakdown.isLoading}
              error={breakdown.error}
              rows={breakdown.breakdown?.instances ?? null}
              total={breakdown.breakdown?.total_instances ?? null}
              strategy={breakdown.breakdown?.strategy ?? null}
            />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Top SKUs tab
// ---------------------------------------------------------------------------

interface TopSkusListProps {
  isLoading: boolean
  error: string | null
  rows: ReadonlyArray<TopSkuRow> | null
  total: number | null
}

function TopSkusList({ isLoading, error, rows, total }: TopSkusListProps) {
  const paged = usePagedSlice(rows ?? EMPTY_ROWS)
  if (isLoading) return <SkeletonRows />
  if (error) {
    return <p className="text-sm text-destructive"><span>Failed to load Top SKUs:</span> {error}</p>
  }
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-muted-foreground italic">No SKUs in this bundle for the selected period.</p>
  }
  return (
    <div>
      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b">
              <th className="text-left px-3 py-2 font-medium">SKU</th>
              <th className="text-left px-3 py-2 font-medium">Title</th>
              <th className="text-right px-3 py-2 font-medium">Units</th>
              <th className="text-right px-3 py-2 font-medium">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageRows.map((r) => (
              <tr key={`${r.product_id ?? "_"}|${r.variant_id ?? "_"}|${r.sku ?? "_"}`} className="border-b">
                <td className="px-3 py-2 font-mono text-xs">{r.sku ?? "—"}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.title}</div>
                  {r.variant_title && (
                    <div className="text-xs text-muted-foreground">{r.variant_title}</div>
                  )}
                </td>
                <td className="text-right px-3 py-2 tabular-nums">{fmtInt.format(r.units_sold)}</td>
                <td className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(r.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePager paged={paged} noun="SKUs" serverTotal={total} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Instances tab
// ---------------------------------------------------------------------------

interface InstancesListProps {
  integrationId: string
  periodStart: string
  periodEnd: string
  timezone: string
  isLoading: boolean
  error: string | null
  rows: ReadonlyArray<BundleInstanceRow> | null
  total: number | null
  /**
   * Drives the bundle_sku-specific «single real line item, components are
   * virtual» note in expanded rows. `null` while the breakdown response is
   * still loading — note is suppressed in that case.
   */
  strategy: BundleStrategy | null
}

function InstancesList({
  integrationId,
  periodStart,
  periodEnd,
  timezone,
  isLoading,
  error,
  rows,
  total,
  strategy,
}: InstancesListProps) {
  const isBundleSku = strategy === "bundle_sku"
  // Track which rows are expanded by composite key (order × bundle group).
  // Multiple rows can be open at once — matches the matrix decompose Sheet.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  const paged = usePagedSlice(rows ?? EMPTY_ROWS)

  if (isLoading) return <SkeletonRows />
  if (error) {
    return <p className="text-sm text-destructive"><span>Failed to load instances:</span> {error}</p>
  }
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-muted-foreground italic">No bundle instances for this period.</p>
  }
  return (
    <div>
      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b">
              <th className="w-8 px-1 py-2" aria-label="Expand" />
              <th className="text-left px-3 py-2 font-medium">Order</th>
              <th className="text-left px-3 py-2 font-medium">Processed</th>
              <th className="text-right px-3 py-2 font-medium">Items</th>
              <th className="text-right px-3 py-2 font-medium">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageRows.map((r) => {
              const key = `${r.shopify_order_id}|${r.bundle_key}`
              const isOpen = expanded.has(key)
              return (
                <Fragment key={key}>
                  <tr
                    className="border-b cursor-pointer hover:bg-muted/30"
                    onClick={() => toggle(key)}
                  >
                    <td className="w-8 px-1 py-2 align-top text-muted-foreground">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4" aria-hidden />
                      ) : (
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">
                        {/* Stop propagation so clicking the order link doesn't
                            also toggle the row's expansion state. */}
                        <span onClick={(e) => e.stopPropagation()}>
                          <OrdersLink
                            integrationId={integrationId}
                            monthStartUtc={periodStart}
                            monthEndUtc={periodEnd}
                            orderIds={[r.shopify_order_id]}
                          >
                            {r.order_name ?? r.shopify_order_id}
                          </OrdersLink>
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground font-mono break-all">{r.bundle_key}</div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                      {formatTimestampInTz(r.processed_at, timezone)}
                    </td>
                    <td className="text-right px-3 py-2 tabular-nums">{fmtInt.format(r.items_count)}</td>
                    <td className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(r.revenue)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b bg-muted/10">
                      <td colSpan={5} className="px-0 py-0">
                        <InstanceLineItemsBreakdown
                          lineItems={r.line_items}
                          isBundleSku={isBundleSku}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      <TablePager paged={paged} noun="instances" serverTotal={total} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Standalone tab
// ---------------------------------------------------------------------------

interface StandaloneBreakdownViewProps {
  isLoading: boolean
  error: string | null
  breakdown: StandaloneBreakdown | null
}

/**
 * Renders the per-family standalone breakdown. For composite bundles
 * (multiple family components) this surfaces one section per family so the
 * user can see exactly which SKUs make up the Standalone column. For
 * bundles whose composition has no family components at all (pure SKU
 * list), the response carries an empty `families` array and we render a
 * hint that per-family breakdown isn't applicable — same pattern as the
 * matrix decompose Sheet's bundle_sku family hint.
 */
function StandaloneBreakdownView({
  isLoading,
  error,
  breakdown,
}: StandaloneBreakdownViewProps) {
  if (isLoading) return <SkeletonRows />
  if (error) {
    return (
      <p className="text-sm text-destructive"><span>Failed to load standalone breakdown:</span> {error}</p>
    )
  }
  if (!breakdown) return null
  if (breakdown.families.length === 0) {
    // Bundle composition has no family components — per-family standalone
    // isn't a thing here. Surface explicitly so the empty state isn't
    // mistaken for «no sales».
    return (
      <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2 font-medium text-foreground">
          <Package className="h-4 w-4" />
          <span>Standalone breakdown not applicable</span>
        </div>
        <p className="mt-1 text-xs">
          This bundle&apos;s composition has no family components — its standalone
          column would just mirror a SKU-list, not a separately-resolvable family.
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-4">
      {breakdown.families.map((fam) => (
        <StandaloneFamilySection key={fam.family_key} family={fam} />
      ))}
    </div>
  )
}

function StandaloneFamilySection({
  family,
}: {
  family: StandaloneBreakdown["families"][number]
}) {
  const hasRows = family.top_skus.length > 0
  const paged = usePagedSlice(family.top_skus)
  return (
    <section className="space-y-2">
      <header className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold break-words">{family.family_label}</h3>
          <div className="font-mono text-[11px] text-muted-foreground break-all">
            {family.family_key}
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground tabular-nums whitespace-nowrap">
          <div>
            {fmtInt.format(family.units_sold)} <span>units ·</span> {fmtCurrency.format(family.total_revenue)}
          </div>
        </div>
      </header>
      {hasRows ? (
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="border-b">
                <th className="text-left px-3 py-2 font-medium">SKU</th>
                <th className="text-left px-3 py-2 font-medium">Title</th>
                <th className="text-right px-3 py-2 font-medium">Units</th>
                <th className="text-right px-3 py-2 font-medium">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {paged.pageRows.map((r) => (
                <tr
                  key={`${r.product_id ?? "_"}|${r.variant_id ?? "_"}|${r.sku ?? "_"}`}
                  className="border-b"
                >
                  <td className="px-3 py-2 font-mono text-xs">{r.sku ?? "—"}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.title}</div>
                    {r.variant_title && (
                      <div className="text-xs text-muted-foreground">{r.variant_title}</div>
                    )}
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmtInt.format(r.units_sold)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">
                    {fmtCurrency.format(r.revenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs italic text-muted-foreground">
          No standalone sales in this family for this period.
        </p>
      )}
      {hasRows && <TablePager paged={paged} noun="SKUs" serverTotal={family.total_skus} />}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Client-side pagination helpers (shared by all three tabs)
// ---------------------------------------------------------------------------

interface PagedSlice<T> {
  pageRows: ReadonlyArray<T>
  /** 1-based index of the first visible row (0 when empty). */
  from: number
  /** 1-based index of the last visible row. */
  to: number
  /** Number of rows the pager iterates over (the loaded set). */
  total: number
  hasPrev: boolean
  hasNext: boolean
  prev: () => void
  next: () => void
}

/**
 * Slice a fully-loaded list into PAGE_SIZE pages, entirely client-side.
 *
 * Page state is local to the mounted section: it persists across re-renders
 * while the section stays mounted, but resets to page 1 when the section
 * unmounts (tab switch — `TabsContent` has no `forceMount`, so Radix drops the
 * inactive panel) or when the source array reference changes (a refetch on
 * period change, or the section's data arriving). The stable `EMPTY_ROWS`
 * passed by callers during loading keeps the reset effect from firing on every
 * render before data lands.
 */
function usePagedSlice<T>(rows: ReadonlyArray<T>): PagedSlice<T> {
  const [page, setPage] = useState(0)
  useEffect(() => {
    setPage(0)
  }, [rows])

  const total = rows.length
  const { start, from, to, hasPrev, hasNext } = computePageWindow(total, page, PAGE_SIZE)

  return {
    pageRows: rows.slice(start, start + PAGE_SIZE),
    from,
    to,
    total,
    hasPrev,
    hasNext,
    prev: () => setPage((p) => Math.max(0, p - 1)),
    next: () => setPage((p) => p + 1),
  }
}

interface TablePagerProps {
  paged: PagedSlice<unknown>
  /** Plural noun for the count line, e.g. "SKUs" / "instances". */
  noun: string
  /**
   * True row count on the server when it exceeds the loaded set (server safety
   * cap hit). Surfaced so the footer never implies the loaded set is complete.
   */
  serverTotal?: number | null
}

/**
 * Shared footer: "Showing 1–25 of N {noun}" + Prev/Next. Buttons only render
 * when there is more than one page. When the server capped the result, the
 * loaded count is qualified ("loaded") and the true total appended, so the cap
 * reads unambiguously (e.g. "Showing 1–25 of 1,000 loaded SKUs · 1,500 total")
 * rather than as two bare adjacent numbers.
 */
function TablePager({ paged, noun, serverTotal }: TablePagerProps) {
  const { from, to, total, hasPrev, hasNext, prev, next } = paged
  const showButtons = hasPrev || hasNext
  const capped = serverTotal != null && serverTotal > total
  return (
    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>
        {showButtons
          ? `Showing ${fmtInt.format(from)}–${fmtInt.format(to)} of ${fmtInt.format(total)} ${capped ? "loaded " : ""}${noun}`
          : `${fmtInt.format(total)} ${noun}`}
        {capped && ` · ${fmtInt.format(serverTotal as number)} total`}
      </span>
      {showButtons && (
        <div className="flex gap-1">
          <Button type="button" variant="outline" size="sm" disabled={!hasPrev} onClick={prev}>
            Prev
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!hasNext} onClick={next}>
            Next
          </Button>
        </div>
      )}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      <div className="h-8 bg-muted rounded animate-pulse" />
      <div className="h-8 bg-muted rounded animate-pulse" />
      <div className="h-8 bg-muted rounded animate-pulse" />
    </div>
  )
}
